import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogTitle, IconButton, Typography } from '@mui/material';
import type { AppModalProps } from './AppModal.types';

const ICON_BOX_SIZE = 36;
const ICON_BOX_RADIUS = '10px';

export function AppModal({
  open,
  onClose,
  icon,
  iconBg,
  title,
  subtitle,
  headerSlot,
  maxWidth = 'sm',
  disableClose = false,
  closeLabel = 'Close',
  titleId = 'app-modal-title',
  paperSx,
  children,
}: AppModalProps) {
  return (
    <Dialog
      open={open}
      onClose={!disableClose ? onClose : undefined}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      slotProps={{
        paper: { sx: paperSx },
        transition: { onEnter: () => { (document.activeElement as HTMLElement)?.blur(); } },
      }}
      sx={theme => ({
        '& .MuiDialog-paper': { color: theme.palette.text.body },
        '& .MuiInputBase-input': { color: theme.palette.text.body },
        '& .MuiInputLabel-root': { color: theme.palette.text.label },
        '& .MuiInputLabel-root.Mui-focused': { color: 'primary.main' },
        '& .MuiFormHelperText-root': { color: theme.palette.text.label },
        '& .MuiFormHelperText-root.Mui-error': { color: 'error.main' },
        '& .MuiSelect-select': { color: theme.palette.text.body },
        '& .MuiChip-label': { color: theme.palette.text.body },
        '& .MuiIconButton-root': { color: theme.palette.action.active },
        '& .MuiButton-outlined': { color: theme.palette.text.body },
      })}
    >
      <DialogTitle
        id={titleId}
        component="div"
        sx={{ pb: headerSlot ? 1 : undefined, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
      >
        <Box
          sx={theme => ({
            width: ICON_BOX_SIZE,
            height: ICON_BOX_SIZE,
            borderRadius: ICON_BOX_RADIUS,
            background: iconBg ?? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: theme.palette.common.white,
          })}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" color="inherit">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label={closeLabel}
          onClick={onClose}
          disabled={disableClose}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {headerSlot && (
        <Box sx={{ px: 3, pb: 2 }}>{headerSlot}</Box>
      )}

      {children}
    </Dialog>
  );
}
